/**
 * Side-by-side comparison of selected lenses (REQ-WEB-027).
 *
 * Lenses are columns and attributes are rows, which is the orientation that
 * survives adding a lens: a new column extends to the right, where there is
 * horizontal scroll to absorb it, rather than adding a row to every group.
 *
 * **Every applicable attribute, not the columns chosen for the list.** Columns
 * are a filter for scanning many rows; three lenses have room for everything,
 * and the difference someone is hunting is usually in a field they did not
 * think to add.
 *
 * **Both headers stick, for different reasons.** The lens names must stay while
 * scrolling down or you lose which column is which after forty attributes; the
 * attribute names must stay while scrolling across or you lose which row you
 * are reading. Sticking one leaves the other half of the grid unreadable — and
 * the corner cell where they meet needs a higher stacking order than both, or
 * it scrolls under one of them.
 *
 * This is the one place horizontal scrolling is right. REQ-WEB-019 forbids it
 * for *controls*, where it fights the page scroll; this is data, and refusing
 * to scroll would mean showing two lenses on a phone and calling it a
 * comparison. The scroll lives in this container, never on the page body.
 */
import { useEffect, useState } from "preact/hooks";

import { AgreementMark, LegendNote } from "./agreement";
import type { Attribute, MountList } from "./api";
import { formatNumber, formatPartialDate, lensName, UNKNOWN, withUnit } from "./format";
import { CellProvenance, LOAD_FAILED, useFocusSource } from "./provenance";
import { shortLabel } from "./query";

interface DetailAttr {
  key: string;
  label: string;
  datatype: string;
  is_core: boolean;
  is_expected: boolean;
  prominence: number;
  /** Shown in the same row as this attribute, as the lens page does (REQ-WEB-024, D26). */
  shown_with?: string | null;
  state: "known" | "unknown" | "not_applicable";
  value?: number | string | boolean | null;
  unit?: string | null;
  source_id?: number | null;
  competing_values?: number;
  distinct_values?: number;
  distinct_publishers?: number;
  values_agree?: boolean;
  /** Worked out for the camera's format rather than stored: nothing to cite (REQ-WEB-035). */
  computed?: boolean;
  for_format?: string;
}

interface Lens {
  slug: string;
  product_code: string;
  model_name: string;
  manufacturer: { slug: string; name: string };
  mount: { slug: string; name: string };
  coverage: string | null;
  lens_type: string | null;
  announced_on: string | null;
  groups: { key: string; attributes: DetailAttr[] }[];
  completeness: { expected_applicable: number; expected_known: number; percent: number | null };
}

function present(a: DetailAttr | undefined, reg: Attribute | undefined): string {
  if (!a) return UNKNOWN;
  if (a.state === "not_applicable") return "n/a";
  if (a.state !== "known" || a.value === null || a.value === undefined) return UNKNOWN;
  if (typeof a.value === "boolean") return a.value ? "Yes" : "No";
  // A release date at the precision recorded, never completed (REQ-DM-028).
  if (a.datatype === "date" && typeof a.value === "string") return formatPartialDate(a.value);
  if (typeof a.value === "string") {
    return reg?.options.find((o) => o.value === a.value)?.label ?? a.value;
  }
  const factor = reg?.display_factor ?? null;
  const shown = factor && factor !== 0 ? a.value / factor : a.value;
  const text = formatNumber(shown, reg?.display_step ?? null);
  // The shared rule for units, so this page and the list cannot disagree.
  return withUnit(reg, text);
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const humanise = (s: string | null) => (s ? titleCase(s.replace(/_/g, " ")) : UNKNOWN);

/**
 * One value in the comparison, opening its source as a value in the list does
 * (REQ-WEB-005; REQ-WEB-027 as amended by D26, #210), with the list's own
 * citation panel and keyboard caption. A value that cannot apply has nothing
 * to cite.
 */
function CompareCell(props: {
  a: DetailAttr | undefined;
  text: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { a, text } = props;
  const unknown = text === UNKNOWN;
  const focus = useFocusSource(a?.source_id ?? null, unknown);
  const cls = unknown ? "cmp-unknown" : text === "n/a" ? "cmp-na" : "";
  // A rule says it cannot apply: nothing to cite. A source that says so for this
  // lens (REQ-DM-008, D45) is cited and opens like any value.
  if (!a || (a.state === "not_applicable" && a.source_id == null)) {
    return <td class={cls}>{text}</td>;
  }
  // A category judged for the camera's format is the site's own arithmetic, not a
  // claim a source made, so it has no citation to open (REQ-WEB-035).
  // The explanation is a visible footnote under the table, not hover text (#213).
  if (a.computed) return <td class={cls}>{text}</td>;
  const competing = a.competing_values ?? 0;
  return (
    <td class={`cmp-cell ${cls}`}>
      <button type="button" class="cmp-value" aria-expanded={props.open}
              {...focus.focusProps} onClick={props.onToggle}>
        {text}
        <AgreementMark competing={competing} distinct={a.distinct_values ?? competing}
                       publishers={a.distinct_publishers ?? competing} agree={a.values_agree} />
      </button>
      {focus.captionEl}
    </td>
  );
}

export function Compare(props: {
  slugs: string[];
  registry: Map<string, Attribute>;
  /** For coverage names: "APS-C", not a title-cased slug. */
  mounts: MountList | null;
  onBack: () => void;
  onRemove: (slug: string) => void;
  /** Open a lens's own page (REQ-WEB-027 as amended by D26). */
  onOpen: (slug: string) => void;
  /** The camera's sensor format, so categories are judged on it (REQ-WEB-035). */
  format?: string | null | undefined;
  /** The query a link to a lens carries to keep that camera ("?mount=…&format=…"). */
  lensSearch?: string | undefined;
}) {
  const [lenses, setLenses] = useState<Lens[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [differencesOnly, setDifferencesOnly] = useState(false);
  // One citation open at a time, as "row:lens". It opens on a row of its own
  // under the spec, across the whole table: a phone column is too narrow for
  // it, and inside the cell it wrapped mid-word or spilled over the next lens.
  const [openCell, setOpenCell] = useState<string | null>(null);
  const [fits, setFits] = useState(true);
  const count = lenses?.length ?? props.slugs.length;
  useEffect(() => {
    // The attribute column plus 124 px per lens, inside the page's gutters.
    const measure = () =>
      setFits(window.innerWidth - 32 >= (window.innerWidth >= 560 ? 160 : 110) + count * 124);
    measure();
    addEventListener("resize", measure);
    return () => removeEventListener("resize", measure);
  }, [count]);

  const key = props.slugs.join(",");
  useEffect(() => {
    const ac = new AbortController();
    setLenses(null);
    setError(null);
    scrollTo(0, 0);
    Promise.all(
      props.slugs.map((s) =>
        fetch(`/api/v1/lenses/${s}${props.format ? `?format=${encodeURIComponent(props.format)}` : ""}`,
              { signal: ac.signal }).then((r) =>
          r.ok ? r.json() : Promise.reject(new Error(LOAD_FAILED)),
        ),
      ),
    )
      .then((ls: Lens[]) => setLenses(ls))
      .catch(() => {
        if (!ac.signal.aborted) setError(LOAD_FAILED);
      });
    return () => ac.abort();
  }, [key, props.format]);

  if (props.slugs.length === 0) {
    return (
      <main class="shell">
        <button type="button" class="backlink" onClick={props.onBack}>
          ← Back to the list
        </button>
        <div class="empty">
          <p class="empty-title">Nothing selected to compare.</p>
          <p class="empty-body">Tick two or more lenses in the list, then come back.</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main class="shell">
        <button type="button" class="backlink" onClick={props.onBack}>
          ← Back to the list
        </button>
        <div class="empty">
          <p class="empty-title">{error}</p>
        </div>
      </main>
    );
  }

  if (!lenses) {
    return (
      <main class="shell">
        <p class="loading" role="status">
          Loading…
        </p>
      </main>
    );
  }

  // Attribute order comes from the first lens, which carries every attribute
  // that applies to it. Union across all of them so a field that applies only
  // to one — a zoom's tele aperture beside a prime — is still a row.
  const seen = new Set<string>();
  const groups: { key: string; keys: string[] }[] = [];
  const everyKey = new Set(
    lenses.flatMap((l) => l.groups.flatMap((g) => g.attributes.map((a) => a.key))),
  );
  // A companion (the registry's shown_with) joins its primary's row when the
  // primary is on the page: "no fact appears as two rows" (REQ-WEB-024, D26),
  // as the lens page already does.
  const companionKey = new Map<string, string>();
  for (const lens of lenses) {
    for (const g of lens.groups) {
      for (const a of g.attributes) {
        if (a.shown_with && everyKey.has(a.shown_with)) companionKey.set(a.shown_with, a.key);
      }
    }
  }
  const companions = new Set(companionKey.values());
  for (const lens of lenses) {
    for (const g of lens.groups) {
      let group = groups.find((x) => x.key === g.key);
      if (!group) {
        group = { key: g.key, keys: [] };
        groups.push(group);
      }
      for (const a of g.attributes) {
        if (!seen.has(a.key) && !companions.has(a.key)) {
          seen.add(a.key);
          group.keys.push(a.key);
        }
      }
    }
  }

  const attrOf = (lens: Lens, k: string) =>
    lens.groups.flatMap((g) => g.attributes).find((a) => a.key === k);
  // Short label: this column is the narrowest thing on a phone, and a long
  // label either wraps to three lines or runs under the first value column.
  const labelOf = (k: string) =>
    shortLabel(props.registry.get(k), k) !== k
      ? shortLabel(props.registry.get(k), k)
      : (lenses.flatMap((l) => l.groups.flatMap((g) => g.attributes))
           .find((a) => a.key === k)?.label ?? k);

  /**
   * A row's text for one lens: the attribute, then its companion if the lens
   * has a value for it, "Yes · 4 stops". An unknown part is left out rather
   * than shown as "— · 4 stops", as on the lens page.
   */
  const textOf = (lens: Lens, k: string): string => {
    const own = present(attrOf(lens, k), props.registry.get(k));
    const ck = companionKey.get(k);
    const ca = ck ? attrOf(lens, ck) : undefined;
    if (!ck || !ca || ca.state !== "known") return own;
    const extra = present(ca, props.registry.get(ck));
    return own === UNKNOWN ? extra : `${own} · ${extra}`;
  };

  /** Same rendered text everywhere means there is nothing to compare. */
  // "Differences only" compares the lenses that have a value. A missing value
  // against a present one is a gap in the data, not a difference between the
  // lenses (REQ-WEB-027 as amended by D26, #210). This applies to the identity
  // rows (Mount, Coverage, Announced, …) exactly as it does to spec rows —
  // "Coverage APS-C | APS-C" and "Announced 2021-01-27 | —" are not differences
  // either (B2, 2026-09-29 QA pass, #287).
  const differsAmong = (values: string[]) => {
    const known = values.filter((t) => t !== UNKNOWN);
    return new Set(known).size > 1;
  };
  const differs = (k: string) =>
    differsAmong(lenses.map((l) => textOf(l, k)));

  // "APS-C", not "Aps c". The API already carries the display name; deriving
  // one by title-casing a slug produces exactly the kind of near-miss that
  // makes a data site look careless.
  const coverageName = (slug: string | null) =>
    (slug && props.mounts?.coverages.find((c) => c.slug === slug)?.name) ?? humanise(slug);

  const identity: [string, (l: Lens) => string][] = [
    ["Brand", (l) => l.manufacturer.name],
    ["Mount", (l) => l.mount.name],
    ["Sensor size covered", (l) => coverageName(l.coverage)],
    ["Prime or zoom", (l) => humanise(l.lens_type)],
    ["Announced", (l) => l.announced_on ?? UNKNOWN],
    ["Key specs cited", (l) => `${l.completeness.expected_known} of ${l.completeness.expected_applicable}`],
  ];

  const shownGroups = groups
    .map((g) => ({ ...g, keys: differencesOnly ? g.keys.filter(differs) : g.keys }))
    .filter((g) => g.keys.length > 0);
  const shownIdentity = differencesOnly
    ? identity.filter(([, get]) => differsAmong(lenses.map(get)))
    : identity;

  return (
    <main class="shell compare">
      {/* Every page needs a first-level heading to navigate by (#216). The
          lens names already head the columns, so this one is for screen
          readers and costs no height on a phone. */}
      <h1 class="visually-hidden">
        Comparing {lenses.length} {lenses.length === 1 ? "lens" : "lenses"}
      </h1>
      <div class="compare-head">
        <button type="button" class="backlink" onClick={props.onBack}>
          ← Back to the list
        </button>
        <button
          type="button"
          class="diff-toggle"
          aria-pressed={differencesOnly}
          onClick={() => setDifferencesOnly(!differencesOnly)}
        >
          {differencesOnly ? "Show every spec" : "Differences only"}
        </button>
      </div>

      {/* One scroll context on a phone (REQ-WEB-027 as amended by D26): when the
          columns fit, the table is part of the page; when they do not, it
          scrolls sideways only, never inside a box of its own height. */}
      <div class={fits ? "cmp-fit" : "cmp-scroll"}>
        <table class="cmp">
          <thead>
            <tr>
              <th class="cmp-corner" scope="col">
                <span class="visually-hidden">Specification</span>
              </th>
              {lenses.map((l) => (
                <th key={l.slug} class="cmp-lens" scope="col">
                  <a
                    class="cmp-link"
                    href={`/lens/${l.slug}${props.lensSearch ?? ""}`}
                    onClick={(e) => {
                      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                      e.preventDefault();
                      props.onOpen(l.slug);
                    }}
                  >
                    {/* Maker then model, once (REQ-WEB-031): when the model name
                        already begins with its maker, the maker is not repeated. */}
                    {lensName(l.manufacturer.name, l.model_name) === l.model_name ? null : (
                      <span class="cmp-brand">{l.manufacturer.name}</span>
                    )}
                    <span class="cmp-name">{l.model_name}</span>
                  </a>
                  <button
                    type="button"
                    class="cmp-remove"
                    onClick={() => props.onRemove(l.slug)}
                    aria-label={`Remove ${l.model_name} from the comparison`}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr class="cmp-group">
              <th class="cmp-attr" scope="row" colSpan={1}>
                The lens
              </th>
              {lenses.map((l) => (
                <td key={l.slug} />
              ))}
            </tr>
            {shownIdentity.map(([label, get]) => (
              <tr key={label}>
                <th class="cmp-attr" scope="row">
                  {label}
                </th>
                {lenses.map((l) => (
                  <td key={l.slug}>{get(l)}</td>
                ))}
              </tr>
            ))}

            {shownGroups.map((g) => (
              <>
                <tr class="cmp-group" key={`g-${g.key}`}>
                  <th class="cmp-attr" scope="row">
                    {titleCase(g.key)}
                  </th>
                  {lenses.map((l) => (
                    <td key={l.slug} />
                  ))}
                </tr>
                {g.keys.map((k) => {
                  const opened = lenses.find((l) => openCell === `${k}:${l.slug}`);
                  const oa = opened ? attrOf(opened, k) : undefined;
                  return (
                  <>
                  <tr key={k}>
                    <th class="cmp-attr" scope="row">
                      {labelOf(k)}
                    </th>
                    {lenses.map((l) => {
                      const a = attrOf(l, k);
                      const id = `${k}:${l.slug}`;
                      return (
                        <CompareCell key={l.slug} a={a} text={textOf(l, k)}
                                     open={openCell === id}
                                     onToggle={() => setOpenCell(openCell === id ? null : id)} />
                      );
                    })}
                  </tr>
                  {opened && oa ? (
                    <tr class="cmp-prov-row" key={`${k}-prov`}>
                      <td colSpan={lenses.length + 1}>
                        <p class="cmp-prov-title">
                          {labelOf(k)} · {lensName(opened.manufacturer.name, opened.model_name)}
                        </p>
                        <CellProvenance slug={opened.slug} attributeKey={k}
                                        attr={props.registry.get(k)}
                                        sourceId={oa.source_id ?? null}
                                        competing={oa.competing_values ?? 0}
                                        distinct={oa.distinct_values ?? oa.competing_values ?? 0}
                                        publishers={oa.distinct_publishers ?? oa.competing_values ?? 0}
                                        agree={oa.values_agree}
                                        unknown={present(oa, props.registry.get(k)) === UNKNOWN}
                                        notApplicable={oa.state === "not_applicable"} />
                        {(() => {
                          // The companion's own citation, labelled, as on the lens page.
                          const ck = companionKey.get(k);
                          const ca = ck ? attrOf(opened, ck) : undefined;
                          if (!ck || !ca || ca.state !== "known") return null;
                          return (
                            <>
                              <p class="cmp-prov-title">{labelOf(ck)}</p>
                              <CellProvenance slug={opened.slug} attributeKey={ck}
                                              attr={props.registry.get(ck)}
                                              sourceId={ca.source_id ?? null}
                                              competing={ca.competing_values ?? 0}
                                              distinct={ca.distinct_values ?? ca.competing_values ?? 0}
                                              publishers={ca.distinct_publishers ?? ca.competing_values ?? 0}
                                              agree={ca.values_agree}
                                              unknown={false} />
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  ) : null}
                  </>
                  );
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {differencesOnly && shownIdentity.length === 0 && shownGroups.length === 0 ? (
        <p class="more-note">
          These lenses are identical on every spec that has been cited. That may mean they
          really are, or that nobody has cited the field that separates them.
        </p>
      ) : null}

      {/* Visible, not hover text (#213): only when a sensor size is chosen, since
          that is the only time a category is worked out here (REQ-WEB-035). */}
      {props.format ? (
        <p class="dfoot cmp-note">
          Lens category is worked out by lens-specs for the sensor size you chose.
        </p>
      ) : null}

      <p class="dfoot">
        A dash means nobody has cited that spec yet; it never means zero. “n/a” means
        it cannot apply to that lens.
      </p>
      <LegendNote />
    </main>
  );
}
